import fastdom from 'fastdom';

import store from './store';
import * as mutationTypes from './store/mutation-types';

import mediaListeners from './media-listeners';
import router from './router';
import ScrollTo from './scroll-to';

import {
  ROUTE_NAMES,
  ROUTE_QUERY_PARAMS,
  ROUTE_PATHS,
  // LANDING_ORIGIN,
} from './config';
import { SIZE_HEIGHT_RED_BULL_HEADER } from '../../design-tokens/build/dictionary';
import { USER_UPDATE_USER } from './store/action-types';

// https://github.com/davidjbradshaw/iframe-resizer/tree/V3

/**
 * Communicates with the parent page through iframe resizer v3
 *
 * @class IframeChannel
 */
class IframeChannel {
  /**
   * Creates an instance of IframeChannel.
   *
   * Set some defaults and some things to make staging not completely useless
   *
   * @memberof IframeChannel
   */
  constructor() {
    this.parentHeight = 300;
    this.parentWidth = 1440;
    this.parentScrollTop = 0;
    this.initialized = false;
    this.parentOffsetTop = SIZE_HEIGHT_RED_BULL_HEADER;

    this.scrollUtil = null;

    if (!this.inFrame) {
      this.scrollUtil = new ScrollTo();
    }

    this.$_iframeReady = new Promise((resolve) => {
      this.$_iframeReadyResolve = resolve;

      if (!this.inFrame) {
        resolve();
      }
    });

    this.$_routerReady = new Promise((resolve) => {
      this.$_routerReadyResolve = resolve;
    });

    this.ready = Promise.all([this.$_iframeReady, this.$_routerReady])
      .then(() => this.initResizing());
  }

  get inFrame() { // eslint-disable-line
    return window.parent !== window;
  }

  get viewportHeight() {
    return this.inFrame ? this.parentHeight : mediaListeners.viewportHeight;
  }

  get viewportWidth() {
    return this.inFrame ? this.parentWidth : mediaListeners.viewportWidth;
  }

  get scrollTop() {
    return this.inFrame ? this.parentScrollTop : mediaListeners.scrollTop;
  }

  get viewTop() {
    return this.inFrame ? this.parentOffsetTop : 0;
  }

  get viewportBottom() {
    return this.inFrame ? this.parentHeight : mediaListeners.viewportHeight;
  }


  /**
   * Update the record of what the parent windows dimensions are.
   *
   * @memberof IframeChannel
   */
  updateDimensions() {
    fastdom.mutate(() => {
      store.commit(mutationTypes.WINDOW_MEDIA_SET_SIZE, {
        innerWidth: this.viewportWidth,
        innerHeight: this.viewportHeight,
      });

      store.commit(mutationTypes.WINDOW_MEDIA_SET_SCROLL_TOP, {
        scrollTop: this.scrollTop,
        viewportBottom: this.viewportBottom,
      });
    });
  }

  initResizing() {
    const parent = this.getParent();
    if (parent) {
      parent.size();
      parent.autoResize(true);
      return;
    }
    console.log('outside iframe!');
  }

  /**
   * make up the config for the iframe resizer. we set this in main.js
   *
   * @returns
   * @memberof IframeChannel
   */
  getIframeResizerConfig() {
    if (!this.initialized) {
      const messageCallback = this.messageCallback.bind(this);
      const readyCallback = this.readyCallback.bind(this);

      this.config = {
        log: true,
        messageCallback,
        readyCallback,
        autoResize: false,
        // TODO: try to restrict in prod
        // targetPage: process.env.NODE_ENV === 'production' ? LANDING_ORIGIN : '*',
      };

      this.initialized = true;
    }

    router.onReady(() => {
      this.$_routerReadyResolve();
    });

    return this.config;
  }

  /**
   * Gotta check that the parent reference from iframe resizer is there
   *
   * @returns
   * @memberof IframeChannel
   */
  getParent() { // eslint-disable-line
    if ('parentIFrame' in window) {
      return window.parentIFrame;
    }
    return null;
  }

  /**
   * As soon as everything is hooked up, start updating store with the parent info, location (so we can deep link), and size, (so we can modal)
   *
   * @memberof IframeChannel
   */
  readyCallback() {
    const callback = this.setParentInfo.bind(this);
    this.getParent().getPageInfo(callback);
    // request submission and section
    this.sendMessage('init');

    this.initResizing.bind(this);

    this.$_iframeReadyResolve();
  }

  /**
   * Update the internal record, un debounced because it doesn't affect the dom
   *
   * @param {*} {
   *     offsetTop,
   *     clientHeight,
   *     scrollTop,
   *   }
   * @memberof IframeChannel
   */
  setParentInfo({
    offsetTop,
    clientHeight,
    scrollTop,
  }) {
    this.parentScrollTop = scrollTop;
    this.parentHeight = clientHeight;
    this.parentOffsetTop = offsetTop;
    this.updateDimensions();
  }

  /**
   * Handle all of the messages. Yes, this should have been a switch based on type. Leave me alone.
   *
   * @param {*} message
   * @returns
   * @memberof IframeChannel
   */
  messageCallback(message) { // eslint-disable-line
    console.log('Mesage from parent', message);

    const {
      type,
      submission,
      parentPath,
      user,
      error,
    } = message;

    if (parentPath) {
      store.commit(mutationTypes.SUBMISSION_SET_SHARE_PATH, { parentPath });
    }

    if (submission) {
      router.push({
        name: ROUTE_NAMES.home,
        query: {
          [ROUTE_QUERY_PARAMS.submissionId]: submission
        }
      });
      return;
    }

    if (type === 'scroll') {
      console.log(message.status);
    }

    if (type === 'login') {
      store.dispatch(USER_UPDATE_USER, { user });
      if (error) {
        this.loginPromise.reject();
      } else {
        this.loginPromise.resolve({ user });
      }
    }

    if (type === 'logout') {
      store.dispatch(USER_UPDATE_USER);

      if (error) {
        this.loginPromise.reject();
      } else {
        this.loginPromise.resolve();
      }
    }
  }

  /**
   * Send a message to the parent frame
   *
   * @param {*} type
   * @param {*} data
   * @memberof IframeChannel
   */
  sendMessage(type, data) {
    if (this.getParent()) {
      this.getParent().sendMessage({ type, ...data });
    }
  }

  /**
   * Tell the parent to scroll, or the current window to scroll if you're not in an iframe
   *
   * @param {*} options
   * @returns
   * @memberof IframeChannel
   */
  scrollTo(options) {
    if (!this.inFrame) {
      this.scrollUtil.scroll(options);
      return;
    }

    const targetY = options.target && options.target.getBoundingClientRect().top;
    // const noScroll = options.noScroll ? 1, 0// don't animate scroll;
    console.log('scrollTo', targetY);
    if (this.getParent()) {
      this.getParent().scrollTo(options.noScroll ? options.noScroll : 0, targetY);
    }
  }

  /**
   * Update the parent route based on the child's route so that people can copy paste links to each other
   *
   * @param {*} {
   *     type = 'push',
   *     route
   *   }
   * @returns
   * @memberof IframeChannel
   */
  updateRoute({
    type = 'push',
    route
  }) {
    if (!route) {
      throw new Error('no Route');
    }

    if (this.inFrame) {
      // update parent frame
      console.log('sending', route.query);
      this.sendMessage('setLocation', {
        navigate: type,
        path: ROUTE_PATHS[route.name] || route.name,
        query: route.query || {}
      });
      return;
    }

    // update this frame
    if (type === 'replace') {
      router.replace(route);
      return;
    }

    router.push(route);
  }

  /**
   * Reference to a promise. it will resolve when the login stuff is handled
   *
   * @memberof IframeChannel
   */
  loginPromise = {
    resolve: () => { console.warn('No promise'); },
    reject: () => { console.warn('No promise'); },
  };

  /**
   * Tell the parent frame to login
   *
   * @returns {Promise.<{*}>} resolves with user info
   * @memberof IframeChannel
   */
  login() {
    this.loginPromise.promise = new Promise((resolve, reject) => {
      this.sendMessage('login');
      this.loginPromise.resolve = resolve;
      this.loginPromise.reject = reject;
    });

    return this.loginPromise.promise;
  }

  /**
   * Tell the parent frame to log out, we never do this.
   *
   * @returns
   * @memberof IframeChannel
   */
  logout() {
    this.loginPromise.promise = new Promise((resolve, reject) => {
      this.sendMessage('logout');
      this.loginPromise.resolve = resolve;
      this.loginPromise.reject = reject;
    });

    return this.loginPromise.promise;
  }

  /**
   * Force the Red Bull header to be solid or remove force
   *
   * @param {*} direction
   * @memberof IframeChannel
   */
  solidHeader(direction) {
    this.sendMessage('solidHeader', {
      direction
    });
  }

  forceResize() {
    const parent = this.getParent();
    if (parent) {
      console.log('force');
      parent.size();
    }
  }
}

export default new IframeChannel();
